import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  ChevronRight,
  Clock,
  GraduationCap,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Sparkles,
  Footprints,
  Repeat2,
  HeartPulse,
  Layers,
  Activity,
  Scissors,
  CircleDot,
  AlertTriangle,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/nail-removal-surgery-mumbai";
const CORN_PATH = "/corn-removal-treatment-mumbai";
const WART_PATH = "/wart-removal-mumbai";
const DIABETIC_FOOT_PATH = "/diabetic-foot-surgery-in-mumbai";

const NAIL_FAQS = [
  {
    question: "Is nail removal surgery painful?",
    answer:
      "No. A digital nerve block is administered before the procedure, completely numbing the toe. The numbing injection itself produces brief discomfort at the injection sites. The nail removal is performed when the anesthetic is fully effective and patients feel no pain during the procedure. Mild to moderate pain returns over the first 24 hours as the anesthetic wears off and is managed with standard pain medication.",
  },
  {
    question: "Will my nail grow back after removal?",
    answer:
      "This depends on which procedure was performed. After partial or total avulsion without matrixectomy, the nail regrows from the intact matrix over 3 to 6 months and usually looks normal. After partial matrixectomy, the treated nail edge does not grow back and the nail is permanently narrower on that side. After total matrixectomy, no nail regrows. Dr. Jain explains clearly which procedure is planned and what the nail will look like afterward before the patient consents to surgery.",
  },
  {
    question: "Can an ingrown toenail be treated without surgery?",
    answer:
      "Mild ingrown toenails in their earliest stage, without infection and with minimal embedding, can be managed conservatively with warm soaks, antiseptic, proper nail cutting, and cotton wool packing beneath the nail edge. This approach requires patient compliance with nail cutting technique and appropriate footwear and does not always prevent recurrence. Once infection is established, the nail is deeply embedded, or the problem has recurred, surgical treatment produces faster and more reliable resolution.",
  },
  {
    question: "How long does recovery take after nail surgery?",
    answer:
      "Pain significantly reduces within 3 to 5 days. The wound heals within 1 to 2 weeks for partial avulsion. Matrixectomy sites take 3 to 6 weeks to fully heal as the phenol-treated area heals by secondary intention. Normal footwear resumes when the wound is comfortable, typically 1 to 3 weeks depending on the procedure. New nail growth after total avulsion reaches a presentable length at 3 to 6 months.",
  },
  {
    question: "What is matrixectomy and is it permanent?",
    answer:
      "Matrixectomy destroys the nail matrix, the tissue at the nail root that produces nail growth, in the portion corresponding to the removed nail edge. The most common technique uses phenol, a chemical that is applied directly to the exposed matrix after partial nail removal. This permanently prevents regrowth of the treated nail edge. The remaining nail continues to grow normally. Matrixectomy is the definitive treatment for recurrent ingrown toenail because it eliminates the nail edge that was causing the problem without removing the entire nail.",
  },
  {
    question: "Can I walk after nail removal surgery?",
    answer:
      "Yes. Patients walk out of the clinic on the same day wearing open footwear. Normal walking on a flat surface is possible from the day of surgery. Physical activity, tight shoes, and prolonged standing are avoided for 1 to 2 weeks while the wound is healing. Most patients with desk jobs return to work the day after surgery or the following day.",
  },
  {
    question: "Is nail surgery safe for diabetic patients?",
    answer:
      "Yes, with appropriate precautions specific to diabetic foot care. Diabetic patients have impaired healing and reduced pain sensation, which means wound management after nail surgery requires more careful monitoring than in non-diabetic patients. Dr. Jain manages diabetic patients with wound care protocols adapted to their condition and ensures appropriate follow-up during healing. Diabetic patients should not attempt to self-treat ingrown toenails and should seek professional assessment at the earliest sign of any nail or foot concern.",
  },
  {
    question: "What footwear should I wear after nail surgery?",
    answer:
      "Open-toed sandals or very wide, soft footwear that does not put any pressure on the treated toe are worn for the first 1 to 2 weeks after surgery. Tight shoes, narrow toe boxes, and high heels are avoided until the wound has fully healed. Once healed, comfortable, well-fitted footwear with adequate toe room is recommended to reduce the risk of ingrown toenail recurrence. Shoes that compress the toes are a primary contributing cause of ingrown toenails and changing to better-fitting footwear after surgery significantly reduces recurrence risk.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalProcedure",
      name: "Nail removal surgery",
      description:
        "Local anesthesia partial or total nail avulsion with optional matrixectomy for ingrown toenail, infection, or nail bed pathology under plastic surgical care.",
      procedureType: "SurgicalProcedure",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Nail" },
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "MedicalCondition",
      name: "Ingrown toenail",
      description:
        "Lateral nail edge embedding in the nail fold with pain, swelling, and risk of infection; managed conservatively or with avulsion and matrixectomy.",
      associatedAnatomy: { "@type": "AnatomicalStructure", name: "Toenail" },
      possibleTreatment: ["Conservative management", "Surgical nail avulsion", "Partial or total matrixectomy"],
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: NAIL_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function NailJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: "/reconstructive-surgery-in-mumbai" },
    { name: "Nail Removal Surgery", href: "/nail-removal-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-12 pb-16 md:pb-24" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-medium tracking-widest text-gold uppercase mb-4">Foot Care</p>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-premium-purple leading-[1.15] mb-6">Nail Removal Surgery</h1>
            <p className="text-lg text-deep-navy/70 mb-8">
              Ingrown Toenail &amp; Nail Bed Surgery by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-none bg-gold/10 flex items-center justify-center">
                  <Award className="w-5 h-5 text-gold" />
                </div>
                <span className="text-sm font-medium text-deep-navy">Board Certified</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-none bg-gold/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-gold" />
                </div>
                <span className="text-sm font-medium text-deep-navy">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-none bg-gold/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-gold" />
                </div>
                <span className="text-sm font-medium text-deep-navy">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Nail Surgery" icon="🦶" size="hero"  imageSrc={HERO.nailRemoval} imageAlt="Nail surgery"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl">
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What is Nail Removal Surgery?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Nail removal surgery is a minor procedure that removes part or all of a nail, performed under local anesthesia, to treat conditions that have not responded to conservative management. The most common indication is an ingrown toenail, where the nail edge grows into the surrounding skin causing pain, swelling, and infection. Other indications include severely infected or deformed nails, nails damaged beyond recovery by trauma, and fungal nail infections that have failed repeated courses of medication.
            </p>
            <p>
              The procedure is performed in a clinic setting without hospital admission and takes 20 to 30 minutes. The toe or finger is completely numbed with local anesthetic before any work begins. Patients experience no pain during the procedure and leave the clinic walking on the same day. The appropriate type of nail removal, partial, total, or permanent with destruction of the nail matrix, depends on the underlying condition and whether the goal is nail regrowth or permanent prevention of nail return in the affected area.
            </p>
            <p>
              Dr. Vicky Jain performs nail removal surgery for patients with ingrown toenails, nail infections, traumatic nail injury, and other nail bed conditions. The technique is selected based on the severity of the condition, the patient&apos;s history of recurrence, and whether permanent matrixectomy is the appropriate definitive treatment.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Dr. Jain&apos;s Approach to Nail Surgery</h2>
            <div className="space-y-6 text-deep-navy/70 leading-relaxed">
              <p>
                The most important decision in nail surgery is whether to perform partial removal with the expectation of normal nail regrowth, or permanent matrixectomy to prevent the problematic nail edge from ever growing back. Partial avulsion with nail edge correction is appropriate for a first or second episode of ingrown toenail in a patient with good nail structure. Permanent chemical or surgical matrixectomy is appropriate for patients with recurrent ingrown toenails who have had multiple episodes and want definitive resolution rather than repeated procedures.
              </p>
              <p>
                Dr. Jain discusses both options with the patient before surgery and ensures the patient understands that matrixectomy produces permanent narrowing of the nail on that side. The nail remains present in the center but the treated edge does not grow back. For most patients this is cosmetically acceptable and preferable to ongoing pain and recurrence. Patients who are uncertain about permanent matrixectomy at the initial presentation can start with partial avulsion and return for matrixectomy if the ingrown nail recurs.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #FAFAF8 0%, rgba(250, 250, 248, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-premium-purple">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ConditionsSection() {
  const cards = [
    {
      title: "Ingrown Toenail",
      icon: Footprints,
      copy: "The most common indication. The nail edge grows into the lateral nail fold, causing pain, swelling, and in many cases secondary bacterial infection with pus formation. The big toe is most commonly affected. Conservative measures including soaking, proper nail trimming, and cotton wool packing address mild cases. Surgical removal is needed when the nail is deeply embedded, infection is established, or the problem has recurred multiple times.",
    },
    {
      title: "Recurrent Ingrown Toenail",
      icon: Repeat2,
      copy: "Patients who have had the same ingrown nail treated multiple times conservatively or by partial removal and continue to have recurrence are candidates for permanent matrixectomy to prevent the problematic nail edge from growing back. Each recurrence is typically more inflamed and infected than the last, making earlier definitive treatment the more practical approach.",
    },
    {
      title: "Chronic Nail Infection",
      icon: HeartPulse,
      copy: "Chronic paronychia or nail bed infection that has not resolved with antibiotics and wound care. Removing the nail provides direct access to the infected tissue, allows thorough debridement, and allows the nail bed to heal without the nail maintaining a closed space for infection.",
    },
    {
      title: "Fungal Nail Infection",
      icon: Sparkles,
      copy: "Severe onychomycosis that has not responded to oral antifungal medication. Removing the infected nail allows topical antifungal treatment to reach the nail bed directly and improves clearance rates for resistant fungal infection.",
    },
    {
      title: "Traumatic Nail Injury",
      icon: Activity,
      copy: "Crush injuries, subungual hematoma, or nail avulsion from trauma. Surgical removal of a partially detached or severely damaged nail, drainage of the hematoma if present, and management of the underlying nail bed injury produces better healing outcomes than allowing the traumatized nail to separate spontaneously over weeks.",
    },
    {
      title: "Thickened or Deformed Nail",
      icon: Layers,
      copy: "Nails that have become severely thickened, curved, or deformed from chronic fungal infection, repeated trauma, or nail bed scarring, causing pressure pain in footwear or difficulty with nail care. Removal of the deformed nail provides immediate relief and allows the nail bed to be assessed and managed.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="conditions-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Conditions Treated with Nail Removal Surgery</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PROCEDURE_TYPES = [
  {
    title: "Partial Nail Avulsion",
    icon: Scissors,
    body:
      "The ingrown or problematic nail edge is removed while leaving the central nail intact. The nail plate is cut from the free edge back to the base and the section is extracted. The remaining nail grows normally and covers the majority of the nail bed. Healing takes 2 to 4 weeks. The ingrown section can recur if the nail matrix at the base is not also treated. Used for first presentations and when preservation of the full nail width is important.",
  },
  {
    title: "Total Nail Avulsion",
    icon: CircleDot,
    body:
      "The entire nail plate is removed from the nail bed. Used for severely infected nails, deformed nails, or traumatic nail injuries where the whole nail needs to be removed for the nail bed to heal. The nail regrows from the matrix over 3 to 6 months. New nail growth is normal in most cases. Used when the whole nail is the problem rather than just the edge.",
  },
  {
    title: "Partial Matrixectomy (Phenol or Surgical)",
    icon: Layers,
    body:
      "The nail matrix, the tissue at the nail root that produces nail growth, is destroyed in the specific portion corresponding to the removed nail edge. Chemical matrixectomy using phenol is the most commonly performed technique, applying the chemical directly to the matrix after nail edge removal. Surgical matrixectomy cuts and removes the matrix segment. Both methods permanently prevent regrowth of the treated nail edge. The permanent solution for recurrent ingrown toenail.",
  },
  {
    title: "Total Matrixectomy",
    icon: Minus,
    body:
      "The entire nail matrix is destroyed, preventing all nail regrowth permanently. Used for severely deformed nails, chronic fungal infections with nail bed damage, or when the patient requests permanent removal of the entire nail. The toe heals with a nail-free surface.",
  },
];

function TypesOfRemovalSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="types-of-removal-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types of Nail Removal Surgery</h2>
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-8 max-w-4xl mx-auto">
          {PROCEDURE_TYPES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0" aria-hidden>
                  <item.icon className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
                  <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl">
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">How Nail Removal Surgery is Performed</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 20 to 30 minutes under local anesthesia. A digital nerve block is administered at the base of the toe or finger, completely numbing the digit. This takes 5 to 10 minutes to achieve full anesthesia. A tourniquet is applied briefly to the digit to reduce bleeding during the procedure and improve visibility.
            </p>
            <p>
              For partial avulsion, the nail plate is separated from the nail bed along the planned removal line using a nail elevator. The nail segment is grasped and extracted with a straight pull from the matrix outward. If matrixectomy is being performed, phenol is applied to the exposed matrix in the treated portion for a controlled time to destroy the matrix cells. The area is then thoroughly irrigated to remove the phenol. For total avulsion, the same steps are performed across the full nail width.
            </p>
            <p>
              A sterile dressing is applied. The tourniquet is removed and circulation is confirmed before the patient leaves. Patients walk out of the clinic on the same day wearing open footwear provided or brought for the appointment. Wound care instructions and a dressing change schedule are provided at discharge.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 2",
      description:
        "Mild to moderate pain as the local anesthetic wears off. Standard pain relief medication manages this. The dressing stays in place. Foot elevation reduces swelling. Open footwear is worn.",
      icon: Clock,
    },
    {
      period: "Days 3 to 5",
      description:
        "Pain reduces significantly. Dressing changes begin at home according to the provided instructions. The wound is healing. Most patients resume normal walking with comfortable open footwear.",
      icon: Footprints,
    },
    {
      period: "Week 1 to 2",
      description:
        "Healing is complete for partial avulsion cases. The nail bed is closed and comfortable. Desk work and normal daily activity have resumed. Tight shoes remain avoided.",
      icon: Monitor,
    },
    {
      period: "Weeks 2 to 6",
      description:
        "For matrixectomy cases, the treated edge heals completely over 3 to 6 weeks. For total avulsion, new nail growth begins at 6 to 8 weeks and reaches a cosmetically presentable length at 3 to 6 months.",
      icon: GraduationCap,
    },
  ];
  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function UrgentWarningSection() {
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="urgent-warning-section" aria-labelledby="urgent-heading">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 id="urgent-heading" className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
          When to Seek Urgent Assessment
        </h2>
        <Alert className="rounded-none border-2 border-gold/50 bg-white max-w-4xl mx-auto py-6 px-4 md:px-6">
          <AlertTriangle className="h-5 w-5 text-gold" aria-hidden />
          <AlertTitle className="font-serif text-lg text-premium-purple pl-7 mb-2">Signs that need prompt medical care</AlertTitle>
          <AlertDescription className="pl-7 text-deep-navy/80 space-y-6">
            <p className="leading-relaxed">
              The following signs indicate that a nail problem requires prompt medical assessment rather than home management or waiting for a scheduled appointment. In healthy patients these are uncomfortable and require treatment. In diabetic patients these are potentially serious and require same-day or urgent assessment.
            </p>
            <ul className="list-disc pl-5 space-y-2 m-0">
              <li>Severe pain that is worsening rather than improving with standard pain relief.</li>
              <li>Pus or discharge from around the nail or nail fold.</li>
              <li>Redness spreading beyond the immediate nail area onto the toe or foot.</li>
              <li>Red streaks extending up the toe toward the foot, indicating spreading infection.</li>
              <li>Fever accompanying a nail infection.</li>
              <li>Any nail problem in a diabetic patient, regardless of severity.</li>
            </ul>
            <p className="leading-relaxed">
              Diabetic patients must seek assessment at the earliest sign of any nail or foot problem. Diabetes impairs pain sensation, meaning the infection may be more advanced than the pain level suggests. It also impairs healing and immune response, meaning a minor nail infection in a non-diabetic patient can progress to a serious foot infection in a diabetic patient if not treated promptly and appropriately. Do not attempt to cut or treat an ingrown toenail at home if you have diabetes. Seek specialist assessment.
            </p>
          </AlertDescription>
        </Alert>
      </div>
    </section>
  );
}

const VS_CONSERVATIVE_ROWS = [
  {
    label: "Pain Relief",
    surgery: "Immediate and lasting. Pain from the ingrown edge is eliminated at the time of procedure.",
    conservative: "Temporary. Pain returns when soaking stops or cotton wool packing is removed.",
  },
  {
    label: "Infection Control",
    surgery: "Definitive. Removes the source of the infection alongside the nail edge.",
    conservative: "Manages surface infection with antibiotics but does not remove the mechanical cause.",
  },
  {
    label: "Recurrence",
    surgery: "Low with matrixectomy. Prevents the problematic nail edge from growing back.",
    conservative: "High. The nail edge continues growing and will re-embed.",
  },
  {
    label: "Time to Resolution",
    surgery: "1 to 3 weeks for most cases.",
    conservative: "Weeks to months with ongoing management, often indefinitely.",
  },
  {
    label: "Appropriate For",
    surgery: "Established ingrown toenail with infection, recurrent cases, failed conservative treatment.",
    conservative: "Very early ingrown toenail without infection in a compliant patient willing to modify nail cutting technique and footwear.",
  },
];

function VsConservativeSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="vs-conservative-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Nail Removal Surgery vs. Home and Conservative Treatment
        </h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[18%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Nail Removal Surgery
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  Conservative Treatment
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_CONSERVATIVE_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.surgery}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.conservative}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center md:text-left leading-relaxed">
          Conservative treatment is appropriate for the earliest stage of ingrown toenail before infection has developed. Once pus is present, the nail is deeply embedded, or the problem has recurred, surgical treatment produces faster and more reliable resolution than continued conservative management.
        </p>
      </div>
    </section>
  );
}

function WhoShouldConsiderSection() {
  return (
    <section className="bg-[#FAFAF8] py-10 md:py-14" data-testid="who-should-consider-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">Who Should Consider Nail Removal Surgery?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any patient with an ingrown toenail that has not resolved with conservative measures, is infected, or has recurred is an appropriate candidate for surgical treatment. There is no benefit to delaying surgery in the presence of established infection. Earlier surgical treatment produces faster resolution, less tissue damage from infection, and a cleaner surgical field that reduces healing time. Patients with recurrent ingrown toenails who have been treated conservatively multiple times are appropriate candidates for permanent matrixectomy to end the cycle of recurrence.
            </p>
            <p>
              Patients with thickened, deformed, or traumatically damaged nails causing pain or difficulty with footwear are appropriate candidates regardless of whether infection is present. Diabetic patients with any nail or foot concern should seek specialist assessment promptly rather than waiting to see whether the problem resolves. In diabetic patients, early surgical treatment of an ingrown toenail before infection has developed is significantly safer and simpler than treating an established infection. Patients in good general health without active systemic infection are appropriate for local anesthesia nail surgery in a clinic setting.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Lipoma" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {NAIL_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`nail-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Nail Surgery Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses the severity of the nail problem, the presence of infection, and whether partial avulsion with or without matrixectomy is the appropriate procedure. Diabetic patients receive a management approach specific to their condition. You leave with a clear plan, a realistic picture of the recovery period, and practical guidance on footwear to prevent recurrence.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Corn Removal",
      description: "Treatment of painful foot growths that frequently accompany nail problems in patients with tight footwear.",
      href: CORN_PATH,
    },
    {
      name: "Wart Removal",
      description: "Periungual warts around the nails that are sometimes assessed alongside nail concerns.",
      href: WART_PATH,
    },
    {
      name: "Wound Care",
      description: "Post-operative wound management for patients requiring ongoing care after nail surgery.",
      href: DIABETIC_FOOT_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function NailRemovalSurgeryPage() {
  return (
    <>
      <SEO
        title="Nail Removal Surgery | Ingrown Toenail Treatment by Dr. Vicky Jain"
        description="Ingrown toenail pain, nail infection, or damaged nail causing ongoing problems. Dr. Vicky Jain performs nail removal surgery safely. Book your consultation."
        canonical="/nail-removal-surgery-mumbai"
      />
      <NailJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <ConditionsSection />
      <TypesOfRemovalSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <UrgentWarningSection />
      <VsConservativeSection />
      <WhoShouldConsiderSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
